import { CUSTOM_FEE_TYPE, type Fee, type SelectedFeeOption, type SelectedFeeOptions } from 'app/types/costAndFees.type';
import { EXPIRATION_TIME_DAYS, LOCAL_STORAGE_KEY } from './constants';
import { logError } from '@zg-rentals/log-error';

/**
 * Generates a unique key for storing/retrieving selected fee options.
 * Uses the fee 'type' for standard fees and a sanitized display name for custom fees.
 */
const getOptionKey = (fee: Fee): string => {
  if (!fee || typeof fee.type !== 'string' || fee.type.trim() === '') {
    return `INVALID_FEE_${Date.now()}_${Math.random()}`;
  }

  if (fee.type === CUSTOM_FEE_TYPE) {
    // Use a sanitized display name for custom fees to create a somewhat stable key
    // Note: If displayNames change, the link to stored selection is broken.
    const formattedFeeName = (fee.displayName || 'UNKNOWN_CUSTOM')
      .replace(/ \(.*\)/g, '')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .toUpperCase();
    return `CUSTOM_FEE_${formattedFeeName}`;
  }
  return fee.type;
};

/**
 * Retrieves selected fee options from localStorage, filtering out expired ones.
 * Returns an empty object if nothing is found or an error occurs.
 */
export const getSelectedOptions = (): SelectedFeeOptions => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {};
    }

    const storedSelectedOptions = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedSelectedOptions) {
      const options: SelectedFeeOptions = JSON.parse(storedSelectedOptions);
      const now = Date.now(); // Use Date.now() for consistency

      // Filter out expired options by creating a new object
      const validOptions: SelectedFeeOptions = {};
      Object.keys(options).forEach((optionKey) => {
        const option = options[optionKey];
        // Check if option exists and has an expiration, and if it's still valid
        if (option && option.expiresOn && option.expiresOn >= now) {
          validOptions[optionKey] = option;
        }
      });
      return validOptions;
    }
  } catch (err) {
    logError({
      error: err,
      errorType: 'CostsAndFeesError',
      context: 'Error reading or parsing selected fee options from localStorage',
    });
  }
  return {};
};

/**
 * Returns a function to get a specific selected option from a SelectedFeeOptions state object.
 * This is a pure function that operates on the passed state.
 */
export const getSelectedOptionFrom =
  (selectedOptions: SelectedFeeOptions) =>
  (fee: Fee): SelectedFeeOption | undefined => {
    if (!fee) return undefined; // Handle invalid fee input gracefully
    const optionKey = getOptionKey(fee);
    return selectedOptions[optionKey];
  };

/**
 * Returns a function to update the selected options state object and persist it to localStorage.
 * This handles adding/updating or removing an option and setting the expiration.
 */
export const updateSelectedOptionFor =
  (currentSelectedOptions: SelectedFeeOptions) =>
  (fee: Fee, optionData?: SelectedFeeOption | null): SelectedFeeOptions => {
    if (!fee || typeof fee.type !== 'string' || fee.type.trim() === '') {
      return currentSelectedOptions;
    }

    const optionKey = getOptionKey(fee);
    const newSelectedOptions = { ...currentSelectedOptions };

    if (optionData) {
      const expirationTimestamp = Date.now() + EXPIRATION_TIME_DAYS * 24 * 60 * 60 * 1000;

      newSelectedOptions[optionKey] = {
        ...optionData,
        expiresOn: expirationTimestamp,
      };
    } else {
      delete newSelectedOptions[optionKey];
    }

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newSelectedOptions));
      }
    } catch (err) {
      logError({
        error: err,
        errorType: 'CostsAndFeesError',
        context: 'Error saving selected fee options to localStorage',
      });
    }

    return newSelectedOptions;
  };
